import { SearchBar } from "@legal-500/legal500-design-system";
import {
  DataFilters,
  type ActiveFilter,
  type FilterConfig,
} from "@/components/DataFilters/DataFilters";
import type {
  RecipientFacets,
  RecipientRegionFilter,
  RecipientResponseState,
} from "@/api/campaigns.service";
import {
  MERGED_STATUSES,
  type MergedRecipientStatus,
  mergedStatusLabel,
  RECIPIENT_RESPONSE_STATES,
  responseStateLabel,
} from "./recipient-status";
import styles from "./MailingGroups.module.css";

type Props = {
  facets: RecipientFacets;
  firmRef: string;
  region: RecipientRegionFilter | undefined;
  // Single merged status filter (New / Scheduled / Sent / Failed / Suppressed);
  // the parent maps it onto the right backend query param.
  statusFilter: MergedRecipientStatus | undefined;
  // Survey-response filter, orthogonal to status; maps to the `responseState`
  // backend param (none / partial / full).
  responseFilter: RecipientResponseState | undefined;
  search: string;
  onFirmChange: (value: string) => void;
  onRegionChange: (value: RecipientRegionFilter | undefined) => void;
  onStatusChange: (value: MergedRecipientStatus | undefined) => void;
  onResponseChange: (value: RecipientResponseState | undefined) => void;
  onSearchChange: (value: string) => void;
};

// Sentinel value for the "All …" placeholder option of each filter.
const ALL = "all";

function regionValue(region: RecipientRegionFilter): string {
  return "rankingEntityId" in region ? region.rankingEntityId : String(region.regionId);
}

function regionFilterOf(region: RecipientRegionFilter): RecipientRegionFilter {
  return "rankingEntityId" in region
    ? { rankingEntityId: region.rankingEntityId }
    : { regionId: region.regionId };
}

// Candidate-table filter toolbar. A thin adapter over the shared DataFilters so
// the projection stays lean. Each change applies immediately (the parent re-runs
// the projection query and clears the selection). Free-text search sits inline
// with the dropdowns; the parent debounces it before hitting the API.
export function RecipientFilters({
  facets,
  firmRef,
  region,
  statusFilter,
  responseFilter,
  search,
  onFirmChange,
  onRegionChange,
  onStatusChange,
  onResponseChange,
  onSearchChange,
}: Props) {
  const filters: FilterConfig[] = [
    {
      key: "firm",
      label: "Firm",
      options: [
        { value: ALL, label: "All firms" },
        ...facets.firms.map(f => ({ value: f.firmRef, label: f.firmName ?? f.firmRef })),
      ],
    },
    {
      key: "region",
      label: "Region",
      options: [
        { value: ALL, label: "All regions" },
        // Region ids are numbers; store as strings so DataFilters values stay
        // homogeneous (they are converted back to numbers in onChange).
        ...facets.regions.map(r => ({
          value: regionValue(r),
          label: r.regionName ?? regionValue(r),
        })),
      ],
    },
    {
      key: "status",
      label: "Status",
      options: [
        { value: ALL, label: "All statuses" },
        ...MERGED_STATUSES.map(s => ({ value: s, label: mergedStatusLabel(s) })),
      ],
    },
    {
      key: "response",
      label: "Response",
      options: [
        { value: ALL, label: "All responses" },
        ...RECIPIENT_RESPONSE_STATES.map(s => ({ value: s, label: responseStateLabel(s) })),
      ],
    },
  ];

  const values: Record<string, string | number> = {
    firm: firmRef || ALL,
    region: region === undefined ? ALL : regionValue(region),
    status: statusFilter ?? ALL,
    response: responseFilter ?? ALL,
  };

  const handleChange = (key: string, value: string | number) => {
    const v = String(value);
    switch (key) {
      case "firm":
        onFirmChange(v === ALL ? "" : v);
        break;
      case "region": {
        const facet = facets.regions.find(r => regionValue(r) === v);
        onRegionChange(facet ? regionFilterOf(facet) : undefined);
        break;
      }
      case "status":
        onStatusChange(v === ALL ? undefined : (v as MergedRecipientStatus));
        break;
      case "response":
        onResponseChange(v === ALL ? undefined : (v as RecipientResponseState));
        break;
    }
  };

  // Chip row: only filters that are actually set, with a human-readable value.
  const activeFilters: ActiveFilter[] = [];
  if (search) {
    activeFilters.push({
      key: "search",
      label: "Search",
      value: search,
      displayValue: search,
    });
  }
  if (firmRef) {
    activeFilters.push({
      key: "firm",
      label: "Firm",
      value: firmRef,
      displayValue: facets.firms.find(f => f.firmRef === firmRef)?.firmName ?? firmRef,
    });
  }
  if (region !== undefined) {
    const value = regionValue(region);
    activeFilters.push({
      key: "region",
      label: "Region",
      value,
      displayValue: facets.regions.find(r => regionValue(r) === value)?.regionName ?? value,
    });
  }
  if (statusFilter) {
    activeFilters.push({
      key: "status",
      label: "Status",
      value: statusFilter,
      displayValue: mergedStatusLabel(statusFilter),
    });
  }
  if (responseFilter) {
    activeFilters.push({
      key: "response",
      label: "Response",
      value: responseFilter,
      displayValue: responseStateLabel(responseFilter),
    });
  }

  // Clearing a chip routes back through the same onXChange handlers so the
  // parent's applyFilter (reset offset + clear selection) still fires.
  const handleClearFilter = (key: string) => {
    if (key === "search") onSearchChange("");
    else if (key === "firm") onFirmChange("");
    else if (key === "region") onRegionChange(undefined);
    else if (key === "status") onStatusChange(undefined);
    else if (key === "response") onResponseChange(undefined);
  };

  const handleClearAll = () => {
    onSearchChange("");
    onFirmChange("");
    onRegionChange(undefined);
    onStatusChange(undefined);
    onResponseChange(undefined);
  };

  // Free-text search rendered as the leading field in the dropdown row so it
  // lines up with the Firm/Region/Status selects. A matching label keeps it
  // visually consistent with the labeled dropdowns.
  const searchField = (
    <div className={styles.recipientSearchField}>
      <span className={styles.recipientSearchLabel}>Search</span>
      <SearchBar
        value={search}
        onChange={onSearchChange}
        onClear={() => onSearchChange("")}
        placeholder="Search name, email, or firm…"
      />
    </div>
  );

  return (
    <DataFilters
      filters={filters}
      values={values}
      onChange={handleChange}
      activeFilters={activeFilters}
      onClearFilter={handleClearFilter}
      onClearAll={handleClearAll}
      leading={searchField}
    />
  );
}
